import api from './api'
import type {
  LLMAPIKey,
  LLMAPIKeyListResponse,
  LLMAPIKeyCreateResponse,
  LLMAPIKeyTestResponse,
  LLMAPIKeyCreateRequest,
  LLMAPIKeyUpdateRequest,
  LLMAPIKeyTestRequest,
} from '@/schemas/llm-api-key'
import type { AxiosRequestConfig } from 'axios'

export const llmApiKeyService = {
  /**
   * List all LLM API keys for an organization
   */
  async listApiKeys(
    orgId: string,
    provider?: string,
    config?: AxiosRequestConfig
  ): Promise<LLMAPIKeyListResponse> {
    const response = await api.get<LLMAPIKeyListResponse>(
      `/organizations/${orgId}/llm-api-keys`,
      { params: provider ? { provider } : undefined, ...config }
    )
    return response.data
  },

  /**
   * Get a single LLM API key by ID
   */
  async getApiKey(
    orgId: string,
    apiKeyId: string,
    config?: AxiosRequestConfig
  ): Promise<LLMAPIKey> {
    const response = await api.get<LLMAPIKey>(
      `/organizations/${orgId}/llm-api-keys/${apiKeyId}`,
      config
    )
    return response.data
  },

  /**
   * Create a new LLM API key
   */
  async createApiKey(
    orgId: string,
    data: LLMAPIKeyCreateRequest,
    config?: AxiosRequestConfig
  ): Promise<LLMAPIKeyCreateResponse> {
    const response = await api.post<LLMAPIKeyCreateResponse>(
      `/organizations/${orgId}/llm-api-keys`,
      data,
      config
    )
    return response.data
  },

  /**
   * Update an existing LLM API key
   */
  async updateApiKey(
    orgId: string,
    apiKeyId: string,
    data: LLMAPIKeyUpdateRequest,
    config?: AxiosRequestConfig
  ): Promise<LLMAPIKey> {
    const response = await api.patch<LLMAPIKey>(
      `/organizations/${orgId}/llm-api-keys/${apiKeyId}`,
      data,
      config
    )
    return response.data
  },

  /**
   * Delete an LLM API key
   */
  async deleteApiKey(
    orgId: string,
    apiKeyId: string,
    config?: AxiosRequestConfig
  ): Promise<void> {
    await api.delete(
      `/organizations/${orgId}/llm-api-keys/${apiKeyId}`,
      config
    )
  },

  /**
   * Test an API key without saving it
   */
  async testApiKey(
    orgId: string,
    data: LLMAPIKeyTestRequest,
    config?: AxiosRequestConfig
  ): Promise<LLMAPIKeyTestResponse> {
    const response = await api.post<LLMAPIKeyTestResponse>(
      `/organizations/${orgId}/llm-api-keys/test`,
      data,
      config
    )
    return response.data
  },

  /**
   * Test a saved API key
   */
  async testSavedApiKey(
    orgId: string,
    apiKeyId: string,
    config?: AxiosRequestConfig
  ): Promise<LLMAPIKeyTestResponse> {
    const response = await api.post<LLMAPIKeyTestResponse>(
      `/organizations/${orgId}/llm-api-keys/${apiKeyId}/test`,
      {},
      config
    )
    return response.data
  },
}
